/**
 * Origine d'acquisition : la source publicitaire du clic qui a amené un
 * visiteur, jointe à sa demande d'ENTREPRISE (14/10/2026, point A.3 du
 * rapport ~/stock-co2-audit-rapports/plateforme/google-ads-optimisation-2026-10-14.md,
 * validé par Guillaume le 14/10/2026).
 *
 * POURQUOI. Google Ads ne savait pas quelles dépenses produisaient des
 * clients : aucun formulaire ne gardait le gclid ni la campagne. Avec cette
 * origine sur la fiche contact, on peut rapprocher une convention signée du
 * clic qui l'a amenée, et l'importer dans Google Ads comme conversion hors
 * ligne (scripts/ads/conversions-hors-ligne.ts).
 *
 * RÈGLES
 *   - Lues à l'ARRIVÉE sur le site, dans l'adresse : gclid, gbraid, wbraid,
 *     utm_source, utm_medium, utm_campaign, utm_term. Rien d'autre.
 *   - Gardées 90 jours dans le stockage local du navigateur, SEULEMENT si le
 *     visiteur a accepté la mesure des campagnes publicitaires (bandeau,
 *     src/lib/consent.ts) ET ne s'est pas opposé à la mesure d'audience
 *     (« Ne pas être compté », cookie `sco2_audience_refus`). Refus ou
 *     retrait du consentement : effacées (`clearAdCookies`).
 *   - Envoyées dans le CORPS POST d'une demande d'entreprise (contact,
 *     démonstration, devis, questionnaire), jamais dans une adresse ; le
 *     serveur les re-nettoie (`nettoyerOrigine`) et les range dans le groupe
 *     `origineAcquisition` de la fiche contact, réservé à l'équipe.
 *   - Jamais pour un porteur de projet (simulateur, « Proposer un projet ») :
 *     inutile, Google Ads ne doit pas apprendre à les trouver.
 *
 * Fonctions pures en tête (testées dans scripts/tests/conversions-entreprises.test.ts),
 * accès au navigateur ensuite, chacun protégé : le stockage peut manquer
 * (navigation privée, quota) sans que rien ne casse.
 */
import { CLE_ORIGINE_ACQUISITION, CONSENT_KEY, isConsentExpired } from '@/lib/consent'
import { COOKIE_REFUS } from '@/lib/audience/mesure'

/** Clé du stockage local (listée dans la politique de cookies). */
export const CLE_ORIGINE = CLE_ORIGINE_ACQUISITION

/** 90 jours : la fenêtre maximale d'import d'un clic dans Google Ads. */
export const DUREE_ORIGINE_JOURS = 90
export const DUREE_ORIGINE_MS = DUREE_ORIGINE_JOURS * 24 * 60 * 60 * 1000

/** Paramètres d'adresse lus, et nom du champ correspondant sur la fiche contact. */
export const PARAMETRES_ORIGINE = {
  gclid: 'gclid',
  gbraid: 'gbraid',
  wbraid: 'wbraid',
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_term: 'utmTerm',
} as const

export type ChampOrigine = (typeof PARAMETRES_ORIGINE)[keyof typeof PARAMETRES_ORIGINE]

/** Ce qui est gardé, envoyé et enregistré. `captureLe` : arrivée sur le site (ISO). */
export type OrigineAcquisition = Partial<Record<ChampOrigine, string>> & { captureLe: string }

const CLICS: ReadonlySet<string> = new Set(['gclid', 'gbraid', 'wbraid'])
/** Identifiants de clic Google : lettres, chiffres, tiret et souligné, 200 caractères au plus. */
const FORME_CLIC = /^[A-Za-z0-9_-]{1,200}$/
const LONGUEUR_UTM = 150

/** Valeur nettoyée, ou null si elle n'a pas la forme attendue. */
function valeur(champ: ChampOrigine, brut: unknown): string | null {
  if (typeof brut !== 'string') return null
  const v = brut.trim()
  if (!v) return null
  if (CLICS.has(champ)) return FORME_CLIC.test(v) ? v : null
  // utm_* : texte court, sans caractère de contrôle ni chevron.
  const t = v.replace(/[\u0000-\u001f\u007f<>]/g, '').slice(0, LONGUEUR_UTM).trim()
  return t || null
}

/** Origine lue dans une chaîne de requête (`?gclid=…&utm_source=…`), ou null s'il n'y a rien. */
export function lireOrigineDepuisAdresse(search: string | null | undefined, maintenant: Date = new Date()): OrigineAcquisition | null {
  if (!search) return null
  let params: URLSearchParams
  try {
    params = new URLSearchParams(search)
  } catch {
    return null
  }
  const o: Partial<Record<ChampOrigine, string>> = {}
  for (const [param, champ] of Object.entries(PARAMETRES_ORIGINE) as [keyof typeof PARAMETRES_ORIGINE, ChampOrigine][]) {
    const v = valeur(champ, params.get(param))
    if (v) o[champ] = v
  }
  return Object.keys(o).length ? { ...o, captureLe: maintenant.toISOString() } : null
}

/**
 * Ce que le serveur accepte d'un corps POST : mêmes champs, mêmes formes,
 * date de capture valide et de moins de 90 jours (sinon : maintenant). Rien
 * d'autre ne passe. Null s'il ne reste aucune valeur.
 */
export function nettoyerOrigine(brut: unknown, maintenant: Date = new Date()): OrigineAcquisition | null {
  if (!brut || typeof brut !== 'object' || Array.isArray(brut)) return null
  const b = brut as Record<string, unknown>
  const o: Partial<Record<ChampOrigine, string>> = {}
  for (const champ of Object.values(PARAMETRES_ORIGINE)) {
    const v = valeur(champ, b[champ])
    if (v) o[champ] = v
  }
  if (!Object.keys(o).length) return null
  const t = typeof b.captureLe === 'string' ? Date.parse(b.captureLe) : NaN
  const valide = Number.isFinite(t) && t <= maintenant.getTime() && maintenant.getTime() - t <= DUREE_ORIGINE_MS
  return { ...o, captureLe: valide ? new Date(t).toISOString() : maintenant.toISOString() }
}

/**
 * Sujets (champ `subject` de `contacts`) des porteurs de projet : leur
 * demande ne garde jamais de source publicitaire.
 */
export const SUJETS_PORTEUR: ReadonlySet<string> = new Set(['porteur', 'simulation_foret', 'simulation_agri'])

/** Origine à enregistrer avec une demande reçue par /api/formulaire-contact (null pour un porteur). */
export function origineDeLaDemande(sujet: unknown, brut: unknown, maintenant: Date = new Date()): OrigineAcquisition | null {
  if (SUJETS_PORTEUR.has(String(sujet ?? ''))) return null
  return nettoyerOrigine(brut, maintenant)
}

/**
 * Le visiteur a-t-il accepté la mesure des campagnes publicitaires (case
 * « marketing » du bandeau), sans s'opposer à la mesure ? La source du clic
 * sert à mesurer les annonces : la mesure d'audience seule ne suffit pas
 * (15/10/2026).
 */
export function memorisationPermise(consentement: { analytics?: boolean; marketing?: boolean } | null, opposition: boolean): boolean {
  return Boolean(consentement?.marketing) && !opposition
}

/** Origine gardée encore valable (capturée il y a moins de 90 jours), ou null. */
export function origineValable(brut: string | null, maintenant: Date = new Date()): OrigineAcquisition | null {
  if (!brut) return null
  try {
    const lu = JSON.parse(brut) as { captureLe?: unknown }
    const t = typeof lu?.captureLe === 'string' ? Date.parse(lu.captureLe) : NaN
    if (!Number.isFinite(t)) return null
    const age = maintenant.getTime() - t
    if (age < 0 || age > DUREE_ORIGINE_MS) return null
    return nettoyerOrigine(lu, maintenant)
  } catch {
    return null
  }
}

// ─────────────────────────── Navigateur ───────────────────────────

function consentementLu(): { analytics: boolean; marketing: boolean } | null {
  try {
    const brut = localStorage.getItem(CONSENT_KEY)
    if (!brut) return null
    const c = JSON.parse(brut) as { analytics?: unknown; marketing?: unknown; storedAt?: unknown }
    if (typeof c.storedAt !== 'number' || isConsentExpired(c.storedAt)) return null
    return { analytics: c.analytics === true, marketing: c.marketing === true }
  } catch {
    return null
  }
}

function opposition(): boolean {
  try {
    return document.cookie.split(';').some((c) => c.trim() === `${COOKIE_REFUS}=1`)
  } catch {
    return false
  }
}

/** Les conditions de la mémorisation sont-elles réunies, maintenant, dans ce navigateur ? */
export function memorisationPermiseIci(): boolean {
  if (typeof window === 'undefined') return false
  return memorisationPermise(consentementLu(), opposition())
}

/** Efface l'origine gardée (refus, retrait du consentement, opposition). */
export function effacerOrigine(): void {
  try {
    localStorage.removeItem(CLE_ORIGINE)
  } catch {
    /* stockage indisponible : rien à effacer */
  }
}

/**
 * Garde l'origine d'une arrivée, si c'est permis ; sinon efface toute
 * origine déjà gardée. Une nouvelle arrivée publicitaire remplace la
 * précédente (le dernier clic est celui que Google Ads attend).
 */
export function memoriserOrigine(o: OrigineAcquisition | null): void {
  if (!memorisationPermiseIci()) {
    effacerOrigine()
    return
  }
  if (!o) return
  try {
    localStorage.setItem(CLE_ORIGINE, JSON.stringify(o))
  } catch {
    /* quota ou navigation privée : la demande partira sans origine */
  }
}

/** Origine à joindre au corps POST d'une demande d'entreprise, ou undefined. */
export function origineAJoindre(): OrigineAcquisition | undefined {
  if (!memorisationPermiseIci()) {
    effacerOrigine()
    return undefined
  }
  try {
    const o = origineValable(localStorage.getItem(CLE_ORIGINE))
    if (!o) effacerOrigine()
    return o ?? undefined
  } catch {
    return undefined
  }
}
